import { IComponentOptions, IController, IPromise, IQService, IRootScopeService, IScope } from "angular";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { AssigneeActions } from "@gtmhub/assignees/redux/assignee-actions";
import { ReactionService } from "@gtmhub/comments/services/reaction.service";
import { IRestLayerRequest } from "@gtmhub/core";
import { storage } from "@gtmhub/core/storage";
import { CustomFieldsService } from "@gtmhub/customFields/services/custom-fields.service";
import { EnvironmentService } from "@gtmhub/env";
import { IUIError } from "@gtmhub/error-handling";
import { ICheckinComment, IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IPlugin, PluginContext, PluginService, UIPluginAttachPoint, getPluginIFrameElement, getPluginSize } from "@gtmhub/plugins";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management/models";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { TaskEvents } from "@gtmhub/tasks/task.events";
import { ITeam, TeamService } from "@gtmhub/teams";
import { TeamEvents } from "@gtmhub/teams/events";
import { IUser, UserService, getCurrentUserId } from "@gtmhub/users";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { CreateReactionDTO } from "@webapp/comments";
import { CfMap } from "@webapp/custom-fields/models/custom-fields.models";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { Session } from "@webapp/sessions/models/sessions.model";
import { Task } from "@webapp/tasks/models/tasks.models";

export type PluginGtmhubAdditionalParams = { gtmhubAdditionalParams: { pluginId?: string; flowId?: string; flowName?: PiTrackingFlowNameEnum } };

type JiraIssue = {
  key: string;
  self: string;
  pluginSettings: { pluginId: string };
  fields: {
    status: { name: string };
    issuetype: { name: string };
    assignee: { emailAddress: string; displayName: string };
    description: string;
    summary: string;
    priority: { id: string };
  };
};

export type Item = Metric | IGoal;
export const getOriginFromUrl = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch (err) {
    return null;
  }
};

type PluginSettings = { pluginId: string };
interface IPluginPayload<T, K = unknown> {
  payload: T & K;
  pluginSettings: PluginSettings;
}
interface IGetRequest {
  id: string;
  pluginSettings: PluginSettings;
}

interface IDeleteRequestMetricSnapshot {
  metricId: string;
  snapshotId: string;
}

interface IPatchRequestMetricComment {
  metricHistoryId: string;
  comment: ICheckinComment;
}

interface IPostRequestReaction {
  createReaction: CreateReactionDTO;
  isReactionOnGif: boolean;
}

interface IDeleteRequestReaction {
  reactionName: string;
  targetId: string;
}

const mergeCustomFields = (existing: CfMap, newCustomFields: CfMap): CfMap => ({
  ...existing,
  ...newCustomFields,
});

interface IPluginReduxScope {
  sessionIdMap: Record<string, Session>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
  assigneesIdMap: Record<string, Assignee>;
  assigneesItemsFetched: boolean;
  assigneesItemsError: IUIError;
}

type IPluginStoreState = IAssigneesStoreState & ISessionsStoreState;

const bindStateToScope = (state: IPluginStoreState): IPluginReduxScope => {
  return {
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
    assigneesIdMap: state.assignees.map,
    assigneesItemsFetched: state.assignees.isFetched,
    assigneesItemsError: state.assignees.error,
  };
};

export class PluginCtrl implements IController {
  public accessPoint: UIPluginAttachPoint;
  public context: PluginContext;
  public item: Item;
  public plugin: IPlugin;

  public src: string;
  public pluginId: string;
  public pluginSize: { width: number; height: number };

  private taskEvents: TaskEvents;
  private teamEvents: TeamEvents;
  private okrEvents: OkrEvents;

  static $inject = [
    "$scope",
    "$rootScope",
    "TaskService",
    "GoalService",
    "MetricService",
    "PluginService",
    "UserService",
    "TeamService",
    "CustomFieldsService",
    "AccountResolverService",
    "ReactionService",
    "$q",
    "EnvironmentService",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
  ];

  constructor(
    private $scope: IScope & IPluginReduxScope & IExtendedRootScopeService,
    private $rootScope: IRootScopeService,
    private taskService: TaskService,
    private goalService: GoalService,
    private metricService: MetricService,
    private pluginService: PluginService,
    private userService: UserService,
    private teamService: TeamService,
    private customFieldsService: CustomFieldsService,
    private accountResolverService: AccountResolverService,
    private reactionService: ReactionService,
    private $q: IQService,
    private env: EnvironmentService,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions
  ) {
    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
  }

  $onInit(): void {
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.pluginId = `${this.accessPoint}-${this.plugin.uiPlugin.src}`;
    this.src = this.pluginService.toTrustedResourceUrl(this.plugin.uiPlugin.src);

    this.pluginSize = getPluginSize(this.accessPoint);
    this.taskEvents = new TaskEvents(this.$scope);
    this.teamEvents = new TeamEvents(this.$scope);

    window.addEventListener("message", this.onMessage);
  }

  $onDestroy(): void {
    window.removeEventListener("message", this.onMessage);
  }

  onGoalsChanged(data: GoalsChangedEventArgs): void {
    if (data.reason.startsWith("metric")) {
      this.onMetricUpdated(data);
    } else {
      this.onGoalUpdated(data);
    }
  }

  onMetricUpdated(data: GoalsChangedEventArgs): void {
    this.postMessage(data, "onMetricUpdated");
  }

  onGoalUpdated(data: GoalsChangedEventArgs): void {
    this.postMessage(data, "onGoalUpdated");
  }

  postMessage = (data: unknown, type: string): void => {
    const iframeWindow = getPluginIFrameElement({ plugin: this.plugin, accessPoint: this.accessPoint });
    try {
      iframeWindow.contentWindow.postMessage(
        {
          type,
          data,
        },
        this.plugin.uiPlugin.src
      );
    } catch (e) {
      console.warn(e);
    }
  };

  updateCurrentItem(type: string, item: (IGoal | Metric) & { pluginSettings: { pluginId: string } }): void {
    const pluginId = item.pluginSettings.pluginId;

    if (this.context === "goal") {
      this.goalService.patchGoal(item.id, item as IGoal, { gtmhubAdditionalParams: { pluginId } }).then((updatedItem) => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");

        this.$rootScope.$broadcast("goalsChanged");
        this.postMessage(updatedItem, type);
      });
    }

    if (this.context === "metric") {
      this.metricService.patchMetric(item as Metric, { gtmhubAdditionalParams: { pluginId } }).then((updatedMetric) => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");
        this.postMessage(updatedMetric, type);
      });
    }
  }

  getCurrentItem = (type: string): void => {
    this.postMessage(this.item, type);
  };

  linkIssue = (type: string, issue: JiraIssue): void => {
    const assignee = issue.fields.assignee;
    const newTask = {
      description: issue.fields.description,
      externalId: issue.self,
      externalSystem: "jira",
      name: issue.fields.summary,
      parentId: this.item.id,
      priority: issue.fields.priority ? +issue.fields.priority.id : 0,
      status: issue.fields.status.name,
      taskType: issue.fields.issuetype.name,
      userEmail: assignee?.emailAddress,
      metadata: {
        "owner-email": assignee?.emailAddress,
        "owner-display-name": assignee?.displayName,
        "issue-key": issue.key,
      },
    };

    this.taskService.create(newTask, { gtmhubAdditionalParams: { pluginId: issue.pluginSettings.pluginId } }).then((newTask) => {
      this.taskEvents.broadcastTaskCreated({ task: newTask, sessionId: this.item.sessionId });
      this.postMessage(newTask, "linkIssue");
    });
  };

  processPromise = <T>(promise: IPromise<T>, type: string): void => {
    promise.then(
      (response) => this.postMessage(response, type),
      (error) => this.postMessage(this.pluginService.transformHttpError(error), type)
    );
  };

  setParams = (): { gtmhubAdditionalParams: { pluginId: string } } => ({ gtmhubAdditionalParams: { pluginId: this.plugin.pluginId } });
  getDc = (type: string) => {
    const apiEndpoint = this.env.getApiEndpoint();
    let dc = "eu";

    if (apiEndpoint.indexOf(".staging") > -1) {
      dc = "staging";
    } else {
      dc = this.env.getDC();
    }

    this.postMessage(dc, type);
  };

  getMetric = (type: string, data: IGetRequest): void => this.processPromise(this.metricService.getMetric(data.id, this.setParams()), type);
  getMetrics = (type: string, data: IPluginPayload<IRestLayerRequest>): void =>
    this.processPromise(this.metricService.getMetricsV2(data.payload, this.setParams()), type);
  createMetric = (type: string, data: IPluginPayload<Metric>): void => this.processPromise(this.metricService.createMetric(data.payload, this.setParams()), type);
  updateMetric = (type: string, data: IPluginPayload<Metric>): void => {
    this.metricService.getMetric(data.payload.id).then((metric) => {
      const existingCustomFields = metric.customFields;
      const newCustomFields = data.payload.customFields;

      /** ensure to merge both objects, otherwise it will omit the customfields not specified in payload */
      if (newCustomFields) {
        data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
      }

      this.processPromise(this.metricService.patchMetric(data.payload, this.setParams()), type);
    });
  };
  patchMetricComment = (type: string, data: IPluginPayload<IPatchRequestMetricComment>) => {
    this.processPromise(this.metricService.patchMetricComment(data.payload.metricHistoryId, data.payload.comment), type);
  };
  createMetricReaction = (type: string, data: IPluginPayload<IPostRequestReaction>) => {
    this.processPromise(this.reactionService.createReaction(data.payload.createReaction, { isReactionOnGif: data.payload.isReactionOnGif }), type);
  };
  deleteMetricReaction = (type: string, data: IPluginPayload<IDeleteRequestReaction>) => {
    this.processPromise(this.reactionService.deleteReaction(data.payload.reactionName, data.payload.targetId), type);
  };
  deleteMetric = (type: string, data: IGetRequest): void => {
    this.metricService.getMetric(data.id, this.setParams()).then((metric) => {
      this.processPromise(this.metricService.deleteMetric(metric.goalId, data.id, this.setParams()), type);
    });
  };
  deleteMetricSnapshot = (type: string, data: IPluginPayload<IDeleteRequestMetricSnapshot>): void => {
    this.metricService.getMetric(data.payload.metricId, this.setParams()).then((metric) => {
      this.processPromise(this.metricService.deleteSnapshot(metric.goalId, data.payload.metricId, data.payload.snapshotId), type);
    });
  };
  checkinMetric = (type: string, data: IPluginPayload<Metric, { checkin }>): void => {
    this.metricService.checkin({ id: data.payload.id, goalId: data.payload.goalId } as Metric, data.payload.checkin, this.setParams()).then((newCheckin) => {
      this.goalService.getGoal(data.payload.goalId).then((goal) => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");

        this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
          checkin: newCheckin,
          metric: data.payload,
          goal,
          session: this.$scope.sessionIdMap[data.payload.sessionId],
        });

        this.postMessage(newCheckin, type);
      });
    });
  };

  getGoal = (type: string, data: IGetRequest): void => this.processPromise(this.goalService.getGoal(data.id, null, this.setParams()), type);
  getGoals = (type: string, data: IPluginPayload<IRestLayerRequest>): void => this.processPromise(this.goalService.getGoalsV2(data.payload, this.setParams()), type);
  createGoal = (type: string, data: IPluginPayload<IGoal>): void => this.processPromise(this.goalService.createGoal(data.payload, null, this.setParams()), type);
  updateGoal = (type: string, data: IPluginPayload<IGoal>): void => {
    this.goalService.getGoal(data.payload.id).then((goal) => {
      const existingCustomFields = goal.customFields;
      const newCustomFields = data.payload.customFields;

      /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
      if (newCustomFields) {
        data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
      }

      this.processPromise(this.goalService.patchGoal(data.payload.id, data.payload, this.setParams()), type);
    });
  };
  deleteGoal = (type: string, data: IGetRequest): void => this.processPromise(this.goalService.deleteGoal(data.id, this.setParams()), type);

  getTask = (type: string, data: IGetRequest): void => this.processPromise(this.taskService.getOne(data.id, this.setParams()), type);
  getTasks = (type: string, data: IPluginPayload<IRestLayerRequest>): void => this.processPromise(this.taskService.getTasksV2(data.payload, this.setParams()), type);
  createTask = (type: string, data: IPluginPayload<Task>): void =>
    this.processPromise(
      this.taskService.create(data.payload, this.setParams()).then((task) => this.taskEvents.broadcastTaskCreated({ task, sessionId: this.item.sessionId })),
      type
    );
  updateTask = (type: string, data: IPluginPayload<Task>): void => {
    this.taskService.getOne(data.payload.id).then((task) => {
      const existingCustomFields = task.customFields;
      const newCustomFields = data.payload.customFields;

      /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
      if (newCustomFields) {
        data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
      }

      this.processPromise(
        this.taskService
          .update(data.payload.id, data.payload, this.setParams())
          .then((task) => this.taskEvents.broadcastTaskUpdated({ task, update: data.payload, sessionId: this.item.sessionId })),
        type
      );
    });
  };
  deleteTask = (type: string, data: IGetRequest): void =>
    this.processPromise(
      this.taskService.delete(data.id, this.setParams()).then(() => this.taskEvents.broadcastTaskDeleted({ task: { id: data.id }, sessionId: this.item.sessionId })),
      type
    );

  getUser = (type: string, data: IGetRequest): void => this.processPromise(this.userService.getUser(data.id, { includeShadowedRoles: true }, this.setParams()), type);
  getUsers = (type: string, data: IPluginPayload<IRestLayerRequest>): void => this.processPromise(this.userService.getUsersV2(data.payload, this.setParams()), type);
  getCurrentUser = (type: string): void => this.processPromise(this.userService.getUser(getCurrentUserId(), { includeShadowedRoles: true }, this.setParams()), type);
  createUser = (type: string, data: IPluginPayload<IUser>): void =>
    this.processPromise(
      this.userService.createUser(data.payload, { skipEmail: false }, this.setParams()).then((user) => this.$rootScope.$broadcast("invitedUsers", [user])),
      type
    );
  updateUser = (type: string, data: IPluginPayload<IUser>): void => {
    this.userService.getUser(data.payload.id).then((user) => {
      const existingCustomFields = user.customFields;
      const newCustomFields = data.payload.customFields;

      /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
      if (newCustomFields) {
        data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
      }

      this.processPromise(
        this.userService.patchUser(data.payload.id, data.payload, this.setParams()).then(() => this.$rootScope.$broadcast("usersChanged", true)),
        type
      );
    });
  };
  deleteUser = (type: string, data: IGetRequest): void =>
    this.processPromise(
      this.userService.deleteUser(data.id, this.setParams()).then(() => this.$rootScope.$broadcast("userDeleted", { id: data.id })),
      type
    );

  createTeam = (type: string, data: IPluginPayload<ITeam>): void =>
    this.processPromise(
      this.teamService.createTeam(data.payload, this.setParams()).then((team) => this.teamEvents.broadcastTeamCreated({ team })),
      type
    );
  updateTeam = (type: string, data: IPluginPayload<ITeam>): void => {
    this.teamService.getTeam(data.payload.id).then((team) => {
      const existingCustomFields = team.customFields;
      const newCustomFields = data.payload.customFields;

      /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
      if (newCustomFields) {
        data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
      }

      this.processPromise(
        this.teamService.updateTeam(data.payload, this.setParams()).then((team) => this.teamEvents.broadcastTeamUpdated({ team })),
        type
      );
    });
  };
  deleteTeam = (type: string, data: IGetRequest): void =>
    this.processPromise(
      this.teamService.deleteTeam(data.id, this.setParams()).then(() => this.teamEvents.broadcastTeamDeleted({ teamId: data.id })),
      type
    );

  getSession = (type: string, data: IGetRequest): void => {
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    const sessionFromReduxPromise = this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => this.$scope.sessionIdMap[data.id],
        () => this.$scope.reduxItemsError
      );

    this.processPromise(sessionFromReduxPromise, type);
  };

  getSetting = (type: string, data: IGetRequest): void => this.processPromise(this.pluginService.getPluginStore({ pluginId: this.plugin.pluginId, key: data.id }), type);
  getSettings = (type: string): void => {
    const pluginsPromises = this.plugin.uiPlugin.pluginSettings
      .filter((setting) => setting.key)
      .map((setting) => this.pluginService.getPluginStore({ pluginId: this.plugin.pluginId, key: setting.key }));
    this.processPromise(this.$q.all(pluginsPromises), type);
  };

  getAccountId = (type: string): void => this.postMessage(storage.get("accountId"), type);

  getAccountSettings = (type: string): void => {
    this.postMessage(this.accountResolverService.getAccountData(), type);
  };

  getAccountUrl = (type: string): void => {
    this.postMessage({ accountUrl: window.location.origin }, type);
  };

  getCustomFields = (type: string): void => {
    const params: IRestLayerRequest = {
      fields: "",
      limit: 500,
    };

    this.processPromise(this.customFieldsService.getCustomFieldsV2(params), type);
  };

  getAssigneesByIds = (type: string, data: IPluginPayload<string[]>): void => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    const assigneesFromReduxPromise = this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.assigneesItemsFetched,
        () => this.$scope.assigneesItemsError
      )
      .then(
        () => {
          return data.payload.reduce(
            (payload, id) => {
              const assignee = this.$scope.assigneesIdMap[id];

              if (assignee) {
                payload.items.push(assignee);
                payload.totalCount = payload.items.length;
              }

              return payload;
            },
            { items: [], totalCount: 0 }
          );
        },
        () => this.$scope.assigneesItemsError
      );

    this.processPromise(assigneesFromReduxPromise, type);
  };

  getAssigneeById = (type: string, data: IGetRequest): void => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    const assigneeFromReduxPromise = this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.assigneesItemsFetched,
        () => this.$scope.assigneesItemsError
      )
      .then(
        () => this.$scope.assigneesIdMap[data.id],
        () => this.$scope.assigneesItemsError
      );

    this.processPromise(assigneeFromReduxPromise, type);
  };

  resize = (type: string, data: { payload: { height: number } }): void => {
    const { height } = data.payload;

    const plugin = document.getElementById(this.pluginId);
    plugin.style.height = `${height}px`;

    this.postMessage("Plugin has been resized successfully", type);
  };

  onMessage = (event: MessageEvent): void => {
    /** each plugin should listen to the specific plugin events, ensure that the plugin domain matches when listening/sending events */

    if (event.origin !== getOriginFromUrl(this.plugin.uiPlugin.src)) {
      return;
    }

    const { type, data } = event.data;
    if (!this[type]) {
      throw new Error(`Method with type: ${type} is not implemented in controller.`);
    }

    this[type](type, data);
  };
}

export const PluginComponent: IComponentOptions = {
  controller: PluginCtrl,
  bindings: {
    accessPoint: "@",
    context: "@",
    item: "<",
    plugin: "<",
  },
  template: require("./plugin.component.html"),
};
